import { Mui } from '@osu/react-ui';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useRoleAuthentication from '../../Authentication/hooks/useRoleAuthentication';
import { useGetOneReviewerQuery, useUpdateReviewerMutation } from '../../services/graphql/api';
import ReviewerSelectBox from './selectBox';
import { VISUALLY_HIDDEN_STYLE } from '../../util/styles';

const INITIAL_STATE = {
    experience: "",
    jobType: "",
    groupType: "",
    goals: 0

}
export const ManageReviewer = () => {
    const { isHonorsAdmin, isScholarsAdmin, isHonorsSeasonalManager } = useRoleAuthentication({ autoMode: true, redirectOnUnauthorized: true });
    const reviewerEmployeeId = useParams().reviewerEmployeeId;
    const navigate = useNavigate();
    const { data, isLoading } = useGetOneReviewerQuery(reviewerEmployeeId, { skip: !isHonorsAdmin && !isScholarsAdmin && !isHonorsSeasonalManager });
    const [updateReviewer, { isLoading: isUpdating }] = useUpdateReviewerMutation();

    const [reviewerState, setReviewerState] = useState({ ...INITIAL_STATE });
    const [selectOptions, setSelectOptions] = useState({
        experiences: [],
        jobTypes: [],
        groupTypes: []
    });

    useEffect(() => {
        if (data) {
            setReviewerState({
                experience: data.reviewer.experience,
                jobType: data.reviewer.jobType,
                groupType: data.reviewer.groupType,
                goals: data.reviewer.goals ?? 0,
                reviewerEmployeeId,
            });
            setSelectOptions({
                experiences: data.experiences,
                jobTypes: data.jobTypes,
                groupTypes: data.groupTypes
            });
        }
    }, [data]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setReviewerState(prevState => ({ ...prevState, [name]: value }));
    };

    const handleUpdate = async (returnToPrevPage) => {
        try {
            const res = await updateReviewer({ input: reviewerState })
            if (!res.error && returnToPrevPage) navigate('/reviewers/active')
        } catch (error) {
            console.error('Failed to update reviewer:', error);
        }
    };

    return (
        isLoading ? (
            <Mui.Skeleton height={200} aria-label="Loading reviewer details" />
        ) :
            <Mui.Box>
                <Mui.Typography variant="h6" component="h2" style={{ marginBottom: '1rem' }}>Reviewer: {data?.reviewer.name}</Mui.Typography>
                <Mui.Paper style={{ padding: "1em" }}>
                    <Mui.Box style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                        <ReviewerSelectBox label="Experience" value={reviewerState.experience} array={selectOptions.experiences} handleChange={handleChange} name="experience" />
                        <ReviewerSelectBox label="Job Type" value={reviewerState.jobType} array={selectOptions.jobTypes} handleChange={handleChange} name="jobType" />
                        <ReviewerSelectBox label="Group" value={reviewerState.groupType} array={selectOptions.groupTypes} handleChange={handleChange} name="groupType" />
                        <ReviewerSelectBox label="Goals" value={reviewerState.goals} type="number" handleChange={handleChange} name="goals" />
                    </Mui.Box>
                    <Mui.Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Mui.Box aria-live="polite" aria-atomic="true" style={VISUALLY_HIDDEN_STYLE}>
                            {isUpdating ? 'Updating reviewer, please wait.' : ''}
                        </Mui.Box>
                        <Mui.Button
                            aria-label="Update reviewer"
                            color="primary"
                            variant="contained"
                            sx={{ margin: '1rem' }}
                            onClick={() => handleUpdate(false)}
                            disabled={isUpdating}
                        >
                            {isUpdating ? 'Updating...' : 'Update'}
                        </Mui.Button>
                        <Mui.Button
                            aria-label="Update reviewer and return to reviewers list"
                            color="primary"
                            variant="contained"
                            sx={{ margin: '1rem' }}
                            onClick={() => handleUpdate(true)}
                            disabled={isUpdating}
                        >
                            {isUpdating ? 'Updating...' : 'Update and Return'}
                        </Mui.Button>
                    </Mui.Box>
                </Mui.Paper>
            </Mui.Box>
    );
};

export default ManageReviewer;